import { FormikContextType } from 'formik/dist/types'
import React from 'react'
import RemoveIcon from '@material-symbols/svg-300/rounded/remove.svg'
import DuplicateIcon from '@material-symbols/svg-300/rounded/control_point_duplicate.svg'
import { FieldArray, FieldArrayRenderProps, getIn } from 'formik'
import { useTranslation } from 'react-i18next'

import Button from '../basics/Button/Button'
import ExpandedIndicator from '../basics/ExpandedIndicator/ExpandedIndicator'
import FieldError from '../basics/FieldError/FieldError'
import { TextInputFormik } from '../basics/TextInput/TextInput'
import { SelectTrainersFormik } from '../formElements/SelectTrainers/SelectTrainers'

import styles from './EditLearningItemForm.module.scss'

export type ScheduledCourseEventsFormValues = {
  events: {
    uuid: string | null | undefined
    bookings: number
    maxAttendees: number
    dates: {
      uuid: string | null | undefined
      title: string
      startDate: Date | null | undefined
      endDate: Date | null | undefined
      meetingLink: string | null | undefined
      meetingAddress: string | null | undefined
      trainers: string[]
    }[]
  }[]
}

/**********************************************************************
 * ScheduledCourseEventForm
 **********************************************************************/
const createEvent = (): ScheduledCourseEventsFormValues['events'][number] => ({
  uuid: undefined,
  bookings: 0,
  maxAttendees: 1,
  dates: [createDate()],
})

const cloneEvent = (event: ScheduledCourseEventsFormValues['events'][number]) => ({
  ...event,
  uuid: undefined,
  bookings: 0,
  dates: event.dates.map(cloneDate),
})

const createDate = (): ScheduledCourseEventsFormValues['events'][number]['dates'][number] => ({
  uuid: undefined,
  title: '',
  startDate: null,
  endDate: null,
  meetingAddress: '',
  meetingLink: '',
  trainers: [],
})

const cloneDate = (date: ScheduledCourseEventsFormValues['events'][number]['dates'][number]) => ({
  ...date,
  uuid: undefined,
})

const ScheduledCourseEventForm = React.memo(function ScheduledCourseEventForm({
  event,
  eventIdx,
  arrayHelpers,
}: {
  event: ScheduledCourseEventsFormValues['events'][number]
  eventIdx: number
  arrayHelpers: FieldArrayRenderProps
}) {
  const { t } = useTranslation()

  let eventStartDate: Date | null | undefined = null
  let eventEndDate: Date | null | undefined = null
  for (const { startDate, endDate } of event.dates) {
    if ((startDate?.valueOf() || Number.MAX_SAFE_INTEGER) < (eventStartDate?.valueOf() || Number.MAX_SAFE_INTEGER)) {
      eventStartDate = startDate
    }
    if ((endDate?.valueOf() || 0) > (eventEndDate?.valueOf() || 0)) {
      eventEndDate = endDate
    }
  }

  return (
    <FieldArray
      name={`events.${eventIdx}.dates`}
      render={(dateArrayHelpers) => (
        <details className={styles.eventForm}>
          <summary>
            <ExpandedIndicator horizontal />
            <h2>
              {eventStartDate && eventEndDate
                ? t('format.DATERANGE', { range: { start: eventStartDate, end: eventEndDate } })
                : t('edit learning.new event')}
            </h2>
            <Button small onClick={() => arrayHelpers.remove(eventIdx)} className={styles.rightAlign}>
              <RemoveIcon />
            </Button>
            <Button small onClick={() => arrayHelpers.insert(eventIdx + 1, cloneEvent(event))}>
              <DuplicateIcon />
            </Button>
          </summary>
          <div className={styles.maxAttendeesAndAddDate}>
            <TextInputFormik
              label={t('learning item.field max attendees')}
              name={`events.${eventIdx}.maxAttendees`}
              type="number"
              min={1}
              required
            />
          </div>
          <div>
            {event.dates.map((date, dateIdx) => (
              <div key={dateIdx} className={styles.dateForm}>
                <div className={styles.dateActions}>
                  <Button small onClick={() => dateArrayHelpers.remove(dateIdx)}>
                    <RemoveIcon />
                  </Button>
                  <Button small onClick={() => dateArrayHelpers.insert(dateIdx + 1, cloneDate(date))}>
                    <DuplicateIcon />
                  </Button>
                </div>
                <div className={styles.dateFields}>
                  <div>
                    <TextInputFormik
                      className={styles.dateStart}
                      label={t('learning item.field scheduled course date start date')}
                      type="datetime-local"
                      name={`events.${eventIdx}.dates.${dateIdx}.startDate`}
                      required
                    />
                    <TextInputFormik
                      className={styles.dateEnd}
                      label={t('learning item.field scheduled course date end date')}
                      type="datetime-local"
                      name={`events.${eventIdx}.dates.${dateIdx}.endDate`}
                      required
                    />
                    <TextInputFormik
                      className={styles.dateTitle}
                      label={t('learning item.field scheduled course date title')}
                      name={`events.${eventIdx}.dates.${dateIdx}.title`}
                    />
                  </div>
                  <div>
                    <TextInputFormik
                      label={t('learning item.field meeting address')}
                      name={`events.${eventIdx}.dates.${dateIdx}.meetingAddress`}
                    />
                    <TextInputFormik
                      type="url"
                      label={t('learning item.field meeting link')}
                      name={`events.${eventIdx}.dates.${dateIdx}.meetingLink`}
                    />
                  </div>
                  <div>
                    <SelectTrainersFormik
                      name={`events.${eventIdx}.dates.${dateIdx}.trainers`}
                      label={t('learning item.field scheduled course date trainers')}
                      allowCreate
                    />
                  </div>
                </div>
              </div>
            ))}
            <div className={styles.addDate}>
              <Button small onClick={() => dateArrayHelpers.push(createDate())} disabled={event.bookings > 0}>
                {t('edit learning.cta add date')}
              </Button>
              <FieldError error={getIn(arrayHelpers.form.errors, `events.${eventIdx}.datesArray`)} touched />
            </div>
          </div>
        </details>
      )}
    />
  )
})

/**********************************************************************
 * ScheduledCourseEventsForms
 **********************************************************************/
export default React.memo(function ScheduledCourseEventsForm({
  formik,
}: {
  formik: FormikContextType<ScheduledCourseEventsFormValues>
}) {
  const { t } = useTranslation()

  return (
    <FieldArray
      name="events"
      render={(arrayHelpers) => (
        <section className={styles.events}>
          <header>
            <h2>{t('learning item.scheduled course events')}</h2>
            <Button small filled onClick={() => arrayHelpers.push(createEvent())}>
              {t('edit learning.cta add event')}
            </Button>
          </header>
          {formik.values.events.map((event, eventIdx) => (
            <ScheduledCourseEventForm key={eventIdx} event={event} eventIdx={eventIdx} arrayHelpers={arrayHelpers} />
          ))}
          <FieldError className={styles.error} error={getIn(formik.errors, `eventsArray`)} touched />
        </section>
      )}
    />
  )
})
